import React from 'react';

function AdminMessages({ supabaseClient }) {
    const [messages, setMessages] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [selectedMessage, setSelectedMessage] = React.useState(null);
    const [showModal, setShowModal] = React.useState(false);
    const [filter, setFilter] = React.useState('all'); // all, unread, read

    React.useEffect(() => {
        fetchMessages();
    }, [filter, supabaseClient]);

    const fetchMessages = async () => {
        if (!supabaseClient) return;

        setIsLoading(true);
        setError(null);
        
        try {
            // Use messages table instead of contact_messages
            let query = supabaseClient
                .from('messages')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (filter === 'unread') {
                query = query.eq('is_read', false);
            } else if (filter === 'read') {
                query = query.eq('is_read', true);
            }
            
            const { data, error } = await query;
            
            if (error) throw error;
            
            setMessages(data || []);
        } catch (err) {
            console.error('Error fetching messages:', err);
            setError('Failed to load messages. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMarkAsRead = async (messageId, isRead = true) => {
        if (!supabaseClient) return;
        
        try {
            const { error } = await supabaseClient
                .from('messages')
                .update({ is_read: isRead })
                .eq('id', messageId);
            
            if (error) throw error;
            
            // Update the messages array locally
            setMessages(messages.map(message => 
                message.id === messageId ? { ...message, is_read: isRead } : message
            ));
            
            // Update selected message if it's being viewed
            if (selectedMessage && selectedMessage.id === messageId) {
                setSelectedMessage({ ...selectedMessage, is_read: isRead });
            }
        } catch (err) {
            console.error('Error updating message status:', err);
            alert('Failed to update message status. Please try again.');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!supabaseClient) return;
        
        if (!confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
            return;
        }
        
        try {
            const { error } = await supabaseClient
                .from('messages')
                .delete()
                .eq('id', messageId);
            
            if (error) throw error;
            
            // Remove the message from the array locally
            setMessages(messages.filter(message => message.id !== messageId));
            
            // Close the modal if the deleted message is currently selected
            if (selectedMessage && selectedMessage.id === messageId) {
                handleCloseModal();
            }
        } catch (err) {
            console.error('Error deleting message:', err);
            alert('Failed to delete message. Please try again.');
        }
    };

    const handleViewMessage = async (message) => {
        setSelectedMessage(message);
        setShowModal(true);
        
        // Mark message as read if it's unread
        if (!message.is_read) {
            handleMarkAsRead(message.id);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        const options = { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (isLoading && messages.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-red-700">Error</h2>
                <p className="text-red-600">{error}</p>
                <button 
                    onClick={fetchMessages}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Messages</h2>
                <div className="flex space-x-2">
                    <button 
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1 rounded ${
                            filter === 'all' ? 
                            'bg-gray-800 text-white' : 
                            'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                    >
                        All
                    </button>
                    <button 
                        onClick={() => setFilter('unread')}
                        className={`px-3 py-1 rounded ${
                            filter === 'unread' ? 
                            'bg-blue-600 text-white' : 
                            'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                    >
                        Unread
                    </button>
                    <button 
                        onClick={() => setFilter('read')}
                        className={`px-3 py-1 rounded ${
                            filter === 'read' ? 
                            'bg-green-600 text-white' : 
                            'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                    >
                        Read
                    </button>
                </div>
            </div>

            {/* Messages Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Sender
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subject
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {messages.length > 0 ? (
                                messages.map(message => (
                                    <tr key={message.id} className={`hover:bg-gray-50 ${!message.is_read ? 'bg-blue-50' : ''}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{message.name}</div>
                                            <div className="text-xs text-gray-500">{message.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`text-sm ${!message.is_read ? 'font-semibold' : ''} text-gray-900`}>
                                                {message.subject || 'No Subject'}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate max-w-xs">
                                                {message.message && message.message.substring(0, 60)}
                                                {message.message && message.message.length > 60 ? '...' : ''}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                message.is_read ? 
                                                'bg-green-100 text-green-800' : 
                                                'bg-blue-100 text-blue-800'
                                            }`}>
                                                {message.is_read ? 'Read' : 'Unread'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(message.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => handleViewMessage(message)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                title="View Message"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleMarkAsRead(message.id, !message.is_read)}
                                                className={`${message.is_read ? 'text-gray-600' : 'text-blue-600'} hover:text-blue-900 mr-3`}
                                                title={message.is_read ? 'Mark as Unread' : 'Mark as Read'}
                                            >
                                                <i className={`fas ${message.is_read ? 'fa-envelope' : 'fa-envelope-open'}`}></i>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteMessage(message.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        {filter === 'all' ? (
                                            'No messages found.'
                                        ) : filter === 'unread' ? (
                                            'No unread messages found.'
                                        ) : (
                                            'No read messages found.'
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Message Modal */}
            {showModal && selectedMessage && (
                <div className="fixed inset-0 overflow-y-auto bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Message Details</h3>
                            <button 
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="mb-6">
                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                <h4 className="text-lg font-semibold mb-2">Sender Information</h4>
                                <p><strong>Name:</strong> {selectedMessage.name}</p>
                                <p><strong>Email:</strong> {selectedMessage.email}</p>
                                {selectedMessage.phone && <p><strong>Phone:</strong> {selectedMessage.phone}</p>}
                                <p><strong>Sent:</strong> {formatDate(selectedMessage.created_at)}</p>
                            </div>
                            
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold mb-2">Message</h4>
                                <p className="text-gray-700 mb-2"><strong>Subject:</strong> {selectedMessage.subject || 'No Subject'}</p>
                                <div className="bg-white p-4 rounded border border-gray-200 whitespace-pre-wrap">
                                    {selectedMessage.message}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button 
                                onClick={() => handleMarkAsRead(selectedMessage.id, !selectedMessage.is_read)}
                                className="px-4 py-2 mr-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {selectedMessage.is_read ? 'Mark as Unread' : 'Mark as Read'}
                            </button>
                            <button 
                                onClick={() => handleDeleteMessage(selectedMessage.id)}
                                className="px-4 py-2 mr-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Delete
                            </button>
                            <button 
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminMessages;
